import { useCallback, useMemo, useState } from 'react';
import Form from 'react-bootstrap/Form';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { IoTrash, IoGrid } from 'react-icons/io5';
import { LuPlusCircle, LuRefreshCw, LuSave, LuX } from 'react-icons/lu';

import {
  Button,
  FormTextField,
  FormSelectField,
  FormSwitchField,
  FloatingCard,
  Dropdown,
} from '_components/_core';

import { CustomFieldContainer, FieldGroup } from '../../styles';

import EventRegistrationPage from '_components/Events/components/EventRegistrationPage/EventRegistrationPageContainer';
import { COLUMN_SIZE_OPTIONS, FIELD_TYPE_OPTIONS } from './utilities';

function CustomFields({
  values,
  setFieldValue,
  errors,
  touched,
  handleChange,
  handleBlur,
}) {
  const [selectedField, setSelectedField] = useState(null);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(null);

  const initialLayout = useMemo(() => {
    if (values.form_layout) {
      return values.form_layout;
    }

    return values.custom_fields.map((f, i) => ({
      w: 12,
      h: 1,
      x: 0,
      y: i,
      i: f.name,
      maxH: 1,
    }));
  }, [values]);

  const onLayoutChange = useCallback(
    newLayout => {
      setFieldValue('form_layout', newLayout);
    },
    [setFieldValue],
  );

  // Helper functions for field management
  const addSection = () => {
    setFieldValue('custom_fields', [
      ...(values.custom_fields || []),
      {
        name: `section_${Date.now()}`,
        label: 'Nova Seção',
        type: 'heading',
        required: false,
      },
      {
        name: `divider_${Date.now()}`,
        label: 'Separador',
        type: 'divider',
        required: false,
      },
    ]);
  };

  const addDivider = () => {
    const newDividerId = `divider_${Date.now()}`;

    // Calculate the highest Y position to place the new field at the bottom
    const currentLayout = values.form_layout || [];
    const maxY =
      currentLayout.length > 0
        ? Math.max(...currentLayout.map(item => item.y + item.h))
        : 0;

    setFieldValue('custom_fields', [
      ...(values.custom_fields || []),
      {
        name: newDividerId,
        field_id: newDividerId, // Add a fixed ID that won't change even if name changes
        label: 'Separador',
        type: 'divider',
        required: false,
      },
    ]);

    setFieldValue('form_layout', [
      ...currentLayout,
      {
        w: 12,
        h: 1,
        x: 0,
        y: maxY,
        i: newDividerId,
        maxH: 1,
      },
    ]);
  };

  const addField = useCallback(
    option => {
      const newFieldId = `field_${Date.now()}`;

      const typeHeightMap = {
        text: 3,
        number: 3,
        date: 3,
        email: 3,
        phone: 3,
        select: 3,
        checkbox: 3,
        radio: 3,
        textarea: 4,
        heading: 1,
        divider: 1,
      };

      const height = typeHeightMap[option.value] || 3;

      const isHeadingOrDivider = ['heading', 'divider'].includes(option.value);

      const currentLayout = values.form_layout || [];
      const maxY =
        currentLayout.length > 0
          ? Math.max(...currentLayout.map(item => item.y + item.h))
          : 0;

      setFieldValue('custom_fields', [
        ...(values.custom_fields || []),
        {
          name: newFieldId,
          field_id: newFieldId,
          label: option.defaultName,
          type: option.value,
          required: false,
          options: [],
          mask: '',
          placeholder: '',
          depends_on: '',
          depends_value: '',
        },
      ]);

      setFieldValue('form_layout', [
        ...currentLayout,
        {
          w: 12,
          h: height,
          x: 0,
          y: maxY,
          i: newFieldId,
          maxH: height,
        },
      ]);
    },
    [setFieldValue, values],
  );

  const handleDeleteField = useCallback(
    field => {
      const { name } = field;

      const updatedFields = [...values.custom_fields];

      updatedFields.splice(
        updatedFields.findIndex(f => f.name === name),
        1,
      );

      setFieldValue('custom_fields', updatedFields);
    },
    [setFieldValue, values],
  );

  const addOption = useCallback(
    (field, fieldIndex) => {
      const updatedFields = [...values.custom_fields];

      const options = [...(updatedFields[fieldIndex].options || [])];

      options.push({
        value: `Opção ${options.length + 1}`,
        label: `Opção ${options.length + 1}`,
      });

      updatedFields[fieldIndex] = {
        ...updatedFields[fieldIndex],
        options,
      };

      setFieldValue('custom_fields', updatedFields);
    },
    [setFieldValue, values],
  );

  const removeOption = useCallback(
    (fieldIndex, optionIndex) => {
      const updatedFields = [...values.custom_fields];

      const options = [...(updatedFields[fieldIndex].options || [])];

      options.splice(optionIndex, 1);

      updatedFields[fieldIndex] = {
        ...updatedFields[fieldIndex],
        options,
      };

      setFieldValue('custom_fields', updatedFields);
    },
    [values, setFieldValue],
  );

  const fieldOptions = useMemo(() => {
    return (values.custom_fields || [])
      .filter(field => field.type !== 'heading' && field.type !== 'divider' && field.name)
      .map(field => ({
        value: field.name,
        label: field.label || field.name,
      }));
  }, [values.custom_fields]);

  const renderHeadingField = (field, index) => (
    <CustomFieldContainer key={`heading-${index}`} className="mb-2 bg-light">
      <div className="d-flex justify-content-between align-items-center">
        <h6>Título de Seção</h6>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => handleDeleteField(field)}
        >
          <IoTrash />
        </Button>
      </div>
      <FormTextField
        name={`custom_fields[${index}].label`}
        placeholder="Título da Seção"
        value={field.label}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </CustomFieldContainer>
  );

  const renderDividerField = (field, index) => (
    <div key={`divider-${index}`} className="position-relative mb-4">
      <hr />
      <div className="position-absolute" style={{ top: '-15px', right: 0 }}>
        <Button
          variant="outline-danger"
          size="sm"
          onClick={() => handleDeleteField(index)}
        >
          <IoTrash />
        </Button>
      </div>
    </div>
  );

  const renderFieldOptions = useCallback(
    (field, fieldIndex) => {
      if (!field) {
        return null;
      }

      if (!['select', 'radio', 'checkbox'].includes(field.type)) {
        return null;
      }

      return (
        <Form.Group as={Col} md={12}>
          <Form.Label>Opções</Form.Label>
          {(field.options || []).map((option, optionIndex) => (
            <Row key={`option-${optionIndex}`} className="mb-2">
              <Col xs={6} className="d-flex align-items-center">
                <span className="mr-2">Rótulo</span>
                <FormTextField
                  name={`custom_fields[${fieldIndex}].options[${optionIndex}].label`}
                  placeholder="Rótulo"
                  value={option.label}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </Col>
              <Col xs={6} className="d-flex align-items-center">
                <span className="mr-2">Valor</span>
                <FormTextField
                  name={`custom_fields[${fieldIndex}].options[${optionIndex}].value`}
                  placeholder="Valor"
                  value={option.value}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
                <Button
                  variant="link"
                  className="m-0 p-0 ml-2 text-danger d-flex align-items-center justify-content-center"
                  size="sm"
                  onClick={() => removeOption(fieldIndex, optionIndex)}
                >
                  <LuX size={16} />
                </Button>
              </Col>
            </Row>
          ))}
          <Button
            variant="link"
            size="sm"
            onClick={() => addOption(field, fieldIndex)}
            className="m-0 p-0 ml-1 mt-3 text-muted d-flex align-items-center justify-content-center"
          >
            <LuPlusCircle size={16} className="mr-1" /> Adicionar opção
          </Button>
        </Form.Group>
      );
    },
    [addOption, removeOption, handleChange, handleBlur],
  );

  // Render standard field types (text, select, etc.)
  const renderStandardField = (field, index) => {
    const isSelect =
      field.type === 'select' || field.type === 'radio' || field.type === 'checkbox';

    return (
      <CustomFieldContainer key={`field-${index}`} className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6>Campo #{index + 1}</h6>
          <div>
            <Button
              variant="outline-secondary"
              size="sm"
              className="mr-2"
              onClick={() => {
                const currentSize = field.column_size || 12;
                const sizeMap = { 12: 6, 6: 4, 4: 3, 3: 12 };
                const updatedFields = [...values.custom_fields];
                updatedFields[index] = {
                  ...updatedFields[index],
                  column_size: sizeMap[currentSize],
                };
                setFieldValue('custom_fields', updatedFields);
              }}
            >
              <IoGrid /> {field.column_size || 12}
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => handleDeleteField(index)}
            >
              <IoTrash />
            </Button>
          </div>
        </div>

        <Form.Row>
          <Form.Group as={Col} md={6}>
            <Form.Label>Nome do Campo</Form.Label>
            <FormTextField
              name={`custom_fields[${index}].name`}
              placeholder="ex: phone_number"
              value={field.name}
              onChange={e => {
                // We no longer need to update layout items when name changes
                // Just use the standard handleChange
                handleChange(e);
              }}
              onBlur={handleBlur}
              error={
                touched.custom_fields?.[index]?.name &&
                errors.custom_fields?.[index]?.name
              }
              helperText="Identificador único (sem espaços)"
            />
          </Form.Group>
          <Form.Group as={Col} md={6}>
            <Form.Label>Rótulo</Form.Label>
            <FormTextField
              name={`custom_fields[${index}].label`}
              placeholder="ex: Telefone"
              value={field.label}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.custom_fields?.[index]?.label &&
                errors.custom_fields?.[index]?.label
              }
              helperText="Nome exibido para o usuário"
            />
          </Form.Group>
        </Form.Row>

        <Form.Row>
          <Form.Group as={Col} md={6}>
            <Form.Label>Tipo de Campo</Form.Label>
            <FormSelectField
              name={`custom_fields[${index}].type`}
              placeholder="Selecione o tipo"
              onBlur={handleBlur}
              options={FIELD_TYPE_OPTIONS}
              error={
                touched.custom_fields?.[index]?.type &&
                errors.custom_fields?.[index]?.type
              }
            />
          </Form.Group>
          <Form.Group as={Col} md={6}>
            <Form.Label>Placeholder</Form.Label>
            <FormTextField
              name={`custom_fields[${index}].placeholder`}
              placeholder="ex: Digite seu telefone"
              value={field.placeholder}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.custom_fields?.[index]?.placeholder &&
                errors.custom_fields?.[index]?.placeholder
              }
              helperText="Texto de exemplo no campo"
            />
          </Form.Group>
        </Form.Row>

        <Form.Row>
          <Form.Group as={Col} md={6}>
            <Form.Label>Máscara</Form.Label>
            <FormTextField
              name={`custom_fields[${index}].mask`}
              placeholder="ex: (99) 99999-9999"
              value={field.mask}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.custom_fields?.[index]?.mask &&
                errors.custom_fields?.[index]?.mask
              }
              helperText="Máscara para telefone, data, etc. 9=número, a=letra"
            />
          </Form.Group>
          <Form.Group as={Col} md={6}>
            <Form.Label>Tamanho na Tela</Form.Label>
            <FormSelectField
              name={`custom_fields[${index}].column_size`}
              onBlur={handleBlur}
              options={COLUMN_SIZE_OPTIONS}
              error={
                touched.custom_fields?.[index]?.column_size &&
                errors.custom_fields?.[index]?.column_size
              }
            />
          </Form.Group>
        </Form.Row>

        <Form.Row>
          <Form.Group as={Col} md={6}>
            <Form.Label>Obrigatório?</Form.Label>
            <FormSwitchField
              name={`custom_fields[${index}].required`}
              label="Campo Obrigatório"
              checked={field.required}
              onChange={e => {
                setFieldValue(`custom_fields[${index}].required`, e.target.checked);
              }}
            />
          </Form.Group>
        </Form.Row>

        {/* Conditional Logic */}
        <FieldGroup className="mt-3">
          <h6>Lógica Condicional</h6>
          <small className="text-muted mb-2 d-block">
            Mostrar este campo apenas quando o campo abaixo tiver o valor especificado.
          </small>

          <Form.Row>
            <Form.Group as={Col} md={6}>
              <Form.Label>Depende do Campo</Form.Label>
              <FormSelectField
                name={`custom_fields[${index}].depends_on`}
                placeholder="Escolha um campo"
                value={field.depends_on || ''}
                onBlur={handleBlur}
                options={[
                  { value: '', label: 'Nenhum (sempre mostrar)' },
                  ...fieldOptions,
                ]}
              />
            </Form.Group>

            {field.depends_on && (
              <Form.Group as={Col} md={6}>
                <Form.Label>Valor para Mostrar</Form.Label>
                <FormTextField
                  name={`custom_fields[${index}].depends_value`}
                  placeholder="ex: Sim"
                  value={field.depends_value || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText="Mostrar quando o campo acima tiver este valor"
                />
              </Form.Group>
            )}
          </Form.Row>
        </FieldGroup>

        {isSelect && renderFieldOptions(field, index)}
      </CustomFieldContainer>
    );
  };

  const handleFieldSelected = useCallback((field, index) => {
    setSelectedField(field);
    setSelectedFieldIndex(index);
  }, []);

  const handleToggleVisibility = useCallback(() => {
    setSelectedField(null);
    setSelectedFieldIndex(null);
  }, []);

  const handleClearFields = useCallback(() => {
    setFieldValue('custom_fields', []);
  }, [setFieldValue]);

  return (
    <>
      <div className="custom-fields-container p-3">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex flex-column">
            <h5 className="mb-1">Formulário de inscrição</h5>
            <p className="mb-0 text-muted">
              Configure o formulário de inscrição para este evento.
            </p>
          </div>
          <div className="d-flex">
            <Button
              variant="white"
              className="d-flex align-items-center text-danger"
              onClick={handleClearFields}
            >
              <LuRefreshCw className="mr-2 " />
              Resetar
            </Button>
            <Dropdown
              placement="bottom-end"
              options={FIELD_TYPE_OPTIONS.map(option => ({
                label: option.label,
                value: option.value,
                icon: option.icon,
                onClick: () => addField(option),
              }))}
            >
              <Button
                variant="white"
                className="d-flex align-items-center ml-2 text-success"
              >
                <LuPlusCircle className="mr-2" />
                Adicionar campo
              </Button>
            </Dropdown>
          </div>
        </div>

        {/* {values.custom_fields && values.custom_fields.length > 0 ? (
        values.custom_fields.map((field, index) => {
          if (field.type === 'heading') {
            return renderHeadingField(field, index);
          } else if (field.type === 'divider') {
            return renderDividerField(field, index);
          } else {
            return renderStandardField(field, index);
          }
        })
      ) : (
        <div className="text-center py-5">
          <p className="mb-4">Nenhum campo adicionado ao formulário.</p>
          <Button variant="primary" onClick={addField}>
            <IoAddCircle className="mr-1" /> Adicionar Primeiro Campo
          </Button>
        </div>
      )} */}
      </div>

      <EventRegistrationPage
        preview
        previewEvent={values}
        layout={initialLayout}
        onLayoutChange={onLayoutChange}
        onDeleteField={handleDeleteField}
        onEditField={handleFieldSelected}
      />

      <FloatingCard
        title={`Campo ${selectedFieldIndex + 1}`}
        isVisible={selectedField}
        onClose={handleToggleVisibility}
        onToggleVisibility={handleToggleVisibility}
        bodyClassName="p-3"
      >
        {/* <pre>{JSON.stringify(selectedField, null, 2)}</pre>
        <pre>{JSON.stringify(values, null, 2)}</pre> */}
        <Form.Row className="mb-1">
          <Form.Group as={Col} md={6}>
            <Form.Label>Nome</Form.Label>
            <FormTextField
              name={`custom_fields[${selectedFieldIndex}].label`}
              placeholder="Rótulo do campo"
              value={selectedField?.label}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.label && errors.label}
            />
          </Form.Group>
          <Form.Group as={Col} md={6}>
            <Form.Label>Nome interno</Form.Label>
            <FormTextField
              name={`custom_fields[${selectedFieldIndex}].name`}
              placeholder="Nome do campo"
              value={selectedField?.name}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.name && errors.name}
            />
          </Form.Group>
        </Form.Row>
        <Form.Row>
          <Form.Group as={Col} md={12}>
            <Form.Label>Placeholder</Form.Label>
            <FormTextField
              name={`custom_fields[${selectedFieldIndex}].placeholder`}
              placeholder="Placeholder do campo"
              value={selectedField?.placeholder}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.custom_fields?.[selectedFieldIndex]?.placeholder &&
                errors.custom_fields?.[selectedFieldIndex]?.placeholder
              }
            />
          </Form.Group>
          {/* <Form.Group as={Col} md={6}>
            <Form.Label>Máscara</Form.Label>
            <FormTextField
              name={`custom_fields[${selectedFieldIndex}].mask`}
              placeholder="Máscara do campo"
              value={selectedField?.mask}
              onChange={handleChange}
              onBlur={handleBlur}
              error={
                touched.custom_fields?.[selectedFieldIndex]?.mask &&
                errors.custom_fields?.[selectedFieldIndex]?.mask
              }
            />
          </Form.Group> */}
          {renderFieldOptions(
            values.custom_fields?.[selectedFieldIndex],
            selectedFieldIndex,
          )}
        </Form.Row>
        <hr />
        <Form.Row>
          <Form.Group as={Col} md={6}>
            <Form.Label>Obrigatório?</Form.Label>
            <FormSwitchField
              name={`custom_fields[${selectedFieldIndex}].required`}
              label="Campo Obrigatório"
              checked={selectedField?.required}
              onChange={e => {
                setFieldValue(
                  `custom_fields[${selectedFieldIndex}].required`,
                  e.target.checked,
                );
              }}
            />
          </Form.Group>
        </Form.Row>
        <FieldGroup className="mt-3">
          <h6>Lógica Condicional</h6>
          <small className="text-muted mb-2 d-block">
            Mostrar este campo apenas quando o campo abaixo tiver o valor especificado.
          </small>

          <Form.Row>
            <Form.Group as={Col} md={6}>
              <Form.Label>Depende do Campo</Form.Label>
              <FormSelectField
                name={`custom_fields[${selectedFieldIndex}].depends_on`}
                placeholder="Escolha um campo"
                value={selectedField?.depends_on || ''}
                onBlur={handleBlur}
                options={[
                  { value: '', label: 'Nenhum (sempre mostrar)' },
                  ...fieldOptions,
                ]}
              />
            </Form.Group>

            {selectedField?.depends_on && (
              <Form.Group as={Col} md={6}>
                <Form.Label>Valor para Mostrar</Form.Label>
                <FormTextField
                  name={`custom_fields[${selectedFieldIndex}].depends_value`}
                  placeholder="ex: Sim"
                  value={selectedField?.depends_value || ''}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText="Mostrar quando o campo acima tiver este valor"
                />
              </Form.Group>
            )}
          </Form.Row>
        </FieldGroup>
        <Button
          variant="white"
          className="d-flex align-items-center"
          onClick={() => handleToggleVisibility()}
        >
          <LuSave className="mr-2 text-success" /> Salvar alterações
        </Button>
      </FloatingCard>
    </>
  );
}

export default CustomFields;
